import { useState } from "react";

import { hasSelectionIn, shouldOpenContextMenu } from "@/lib/contextMenu";
import { useMenu } from "@/components/shell/Menu";
import type { Definition, GrammarEntry } from "@/lib/search";
import { AddedPanel } from "@/surfaces/read/AddedPanel";
import { isOnCard } from "@/surfaces/read/onCard";
import { ResultCard } from "@/surfaces/read/ResultCard";

import { GrammarCard } from "./GrammarCard";
import { KindGutter } from "./KindGutter";
import { rowKey, rowTerm } from "./resultKind";
import type { Lookup, MineSource } from "./useLookup";

interface ResultListProps {
  lookup: Lookup;
  /**
   * Where a card added from here inherits its sentence and images. Read supplies the entry the
   * lookup came from; 検索 supplies nothing.
   *
   * A **function**, not a value: Read's source is whichever row is selected, and that moves under
   * the list without the list re-rendering for it.
   */
  mineSource?: () => MineSource | null;
  /** What to say when there is nothing — the two surfaces are asked different questions. */
  empty: React.ReactNode;
  /**
   * Open a 文法 row in the 文法 destination (日本-519). It used to ADD a card.
   *
   * A prop rather than something `useLookup` owns, because it is a navigation and the hook has no
   * nav access — and because only Read passes one: 検索 answers about vocabulary alone now, so a
   * grammar row cannot appear there to be opened.
   */
  onOpenGrammar?: (entry: GrammarEntry) => void;
  /**
   * Whether to draw the 26px kind gutter down the left of every row (日本-519).
   *
   * **Read yes, 検索 no.** The gutter exists to tell two kinds apart at scanning speed; with
   * grammar gone from 検索 every row there is 語彙, so it would say the same word 629 times and
   * cost 26px a row to do it. Where two kinds really are interleaved it is doing its job, and
   * 日本-498's rule holds unchanged: EVERY row carries one there, dictionary rows included, so the
   * list is one shape rather than "plain rows and marked rows".
   */
  showKind?: boolean;
  isPhone?: boolean;
  className?: string;
}

/**
 * The dock's results — one `ResultCard` per row, the expanded `AddedPanel` in place of whichever row
 * is open (日本-497).
 *
 * Lifted out of `Read.tsx` unchanged so 検索 draws exactly the same rows. **The header is
 * deliberately not here**: Read's is the hovered token, its count and the FORM strip, and 検索's is
 * the search field itself. They are different questions about the same list, so each surface keeps
 * its own and this owns only the list.
 */
export function ResultList({
  lookup,
  mineSource,
  empty,
  onOpenGrammar,
  showKind = false,
  isPhone = false,
  className,
}: ResultListProps) {
  const menu = useMenu();
  const { rows, expanded, carded, onCardEntries, busyRow, focusedResult, collapse } = lookup;
  const [expandedGrammar, setExpandedGrammar] = useState<string | null>(null);

  const add = (entry: Definition, index: number) => {
    void lookup.add(entry, index, mineSource?.() ?? null);
  };
  // Cleared here rather than inside `edit`, which `add` also calls right after setting the "already
  // in your deck" message — clearing in there would erase it. A failed `E` on one row otherwise
  // leaves its message sitting over the next row's successfully opened panel.
  const edit = (entry: Definition, index: number) => {
    lookup.setError(null);
    void lookup.edit(entry.term ?? "", index);
  };
  return (
    /*
     * **`[&>*]:shrink-0` — no row may be compressed by the column it is in.**
     *
     * Both surfaces put this list in a flex COLUMN inside a scroller, so every row is a flex item
     * with the default `flex-shrink: 1`: once the rows want more height than the column has, the
     * browser takes it back from them proportionally rather than letting the scroller scroll.
     * Measured on てしまう at 1147 — 手四's row asked for 89px and was given 37, with the badge and
     * the `+ 追加` button cut off inside it.
     *
     * It was invisible until 日本-498, and the reason is worth knowing: a shrunk row without
     * `overflow: hidden` still PAINTS its content, so the damage was rows quietly overlapping
     * rather than anything obviously clipped. The kind gutter needs the row to clip (a rounded box
     * with a flush band down its left side), which turned a long-standing squash into a visible
     * one. The fix is the squash, not the clipping.
     */
    <div className={`${className ?? ""} [&>*]:shrink-0`} data-check="results">
      {rows.length === 0 && empty}
      {rows.map((row, index) => {
        // **The expanded panel replaces whichever row is open, of EITHER kind** (日本-498).
        //
        // It sits above the kind branch rather than inside the vocabulary one, because since
        // grammar cards can be edited the row behind the panel is not always a dictionary entry —
        // and a `term` is the only thing the panel ever took from it.
        //
        // The ROW, not just a term match. One term legitimately spans several rows — an entry
        // whose surface is both a kanji form and a reading comes back from both indexes (see
        // CLAUDE.md), and since 日本-290 three entries written 蝗 each render their own 蝗 row.
        // Rendering a panel at each would give one card two of everything the panel owns: two
        // window paste listeners, so one Ctrl+V uploads twice, and two Esc saves. Anchoring on the
        // first match instead put the panel over a row you did not press.
        const rowHeadword = rowTerm(row);
        if (expanded && rowHeadword === expanded.term && expanded.row === index) {
          return (
            <AddedPanel
              key={`expanded-${expanded.term}-${index}`}
              term={expanded.term}
              noteId={expanded.noteId}
              revision={expanded.revision}
              justAdded={expanded.justAdded}
              inheritedImage={expanded.image}
              onCollapse={() => lookup.setExpanded(null)}
              onUndo={() => void lookup.undo()}
            />
          );
        }

        // A grammar row is its own component: no senses, no readings, no seq, and the collection
        // states mean something different about it. It shares the panel, the badge row and the
        // `+ 追加` corner, so the list still reads as one list rather than two stacked ones.
        if (row.kind === "grammar") {
          const grammar = row.entry;
          return (
            <GrammarCard
              key={rowKey(row, index)}
              entry={grammar}
              focused={index === focusedResult}
              expanded={expandedGrammar === grammar.id}
              busy={busyRow === index}
              isPhone={isPhone}
              onFocus={() => lookup.setFocusedResult(index)}
              onToggle={() =>
                setExpandedGrammar((current) => (current === grammar.id ? null : grammar.id))
              }
              onOpen={() => onOpenGrammar?.(grammar)}
            />
          );
        }

        const entry = row.entry;

        // **Where the gutter is drawn at all, EVERY row carries one** (Alex, 27 Aug 2026) — the
        // wrapper is what makes the list one shape rather than "plain rows and marked rows", and
        // what lets a name row cost nothing to add later. Whether it is drawn is `showKind`, which
        // is a question about the SURFACE rather than about the row: see the prop.
        // `ResultCard` is untouched either way — it renders exactly as it does in Read, in a box
        // 26px narrower when the gutter is there.
        return (
          <div
            key={rowKey(row, index)}
            className={[
              "mr-3 flex overflow-hidden rounded-[var(--r-3)] bg-panel",
              index === focusedResult
                ? "border border-accent-line shadow-[var(--sh-frame)]"
                : "border border-line",
            ].join(" ")}
          >
            {showKind && <KindGutter kind="vocab" />}
            <ResultCard
              bare
              index={index}
              entry={entry}
              signal={carded.get(entry.term ?? "") ?? { state: "unknown" }}
              focused={index === focusedResult}
              collapsed={collapse}
              isPhone={isPhone}
              busy={busyRow === index}
              onFocus={() => lookup.setFocusedResult(index)}
              onCard={isOnCard(onCardEntries, entry.term, entry.seq)}
              onAdd={() => add(entry, index)}
              onEdit={() => edit(entry, index)}
              // The two actions `ActionButton` already offers, reached by the gesture people expect.
              // Nothing new is invented here: `A` and `E` are the same handlers, and `カードを編集`
              // is offered only where the button would be — on a word you actually hold.
              onContextMenu={(event) => {
                if (
                  !shouldOpenContextMenu(event.nativeEvent, {
                    selectionInTarget: hasSelectionIn(event.currentTarget),
                  })
                ) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                lookup.setFocusedResult(index);
                const signal = carded.get(entry.term ?? "") ?? { state: "unknown" as const };
                menu.openAt({ x: event.clientX, y: event.clientY }, [
                  {
                    kind: "item",
                    id: "add",
                    label: "カードを追加",
                    hint: isPhone ? undefined : "A",
                    onSelect: () => add(entry, index),
                  },
                  ...(signal.state === "known"
                    ? [
                        {
                          kind: "item" as const,
                          id: "edit",
                          label: "カードを編集",
                          ...(isPhone ? {} : { hint: "E" }),
                          onSelect: () => edit(entry, index),
                        },
                      ]
                    : []),
                ]);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
